import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import IconButton from "@material-ui/core/IconButton";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import CloseIcon from "@material-ui/icons/Close";
import Finance from "Lib/API/Finance";
import DonationBox from "components/DonationBox/DonationBox";
import { Grid } from "@mui/material";
import Paper from "@mui/material/Paper";
import { PaymentCard } from "pages/MemberLink/PayFromExternalLink/PaymentCard/PaymentCard";
import { PaymentResultPage } from "pages/MemberLink/PayFromExternalLink/PaymentResultPage/PaymentResultPage";
import { PersonalDetailsCard } from "pages/MemberLink/PayFromExternalLink/PersonalDetailsCard/PersonalDetailsCard";
import { SummaryCard } from "pages/MemberLink/PayFromExternalLink/SummaryCard/SummaryCard";
import { MemberPersonalDetailsSchema } from "pages/ValidationSchema/index";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { getAccessTokenSilently } from "utils/functions";
import { checkIsStripe, checkIsTranzila } from "helper/checkIsStripe";
import { useFormik } from "formik";
import { useSnackbar } from "notistack";
import { Fonts, FontWeights, FontFamilies } from "theme/fontConfig";
import { convenienceFeeAmount as calcConvenienceFee } from "helper/convenienceFee";

/* ── map a portal transaction row → chargeSelection item ── */
const rowToSelection = (row) => ({
  chargeId: row?.charge?._id,
  amount: Math.max(
    0,
    (row?.charge?.amount ?? 0) +
      (row?.charge?.discount ?? 0) -
      (row?.charge?.paid ?? 0)
  ),
  Ledger: row?.Ledger,
  SubLedger: row?.SubLedger,
  due_date: row?.charge?.due_date,
  totalAmount: row?.charge?.amount ?? 0,
  totalPaid: row?.charge?.paid ?? 0,
  installmentStatus: false,
  // installment eligibility — portal rows carry these at the top level
  isInstallmentTransaction: row?.isInstallmentTransaction ?? row?.charge?.isInstallment ?? false,
  installmentDetailsTransaction: row?.installmentDetailsTransaction ?? row?.charge?.installmentDetails ?? null,
});

const PortalPayNowModal = ({ open, onClose, data }) => {
  const { enqueueSnackbar } = useSnackbar();
  const isMobile = useMediaQuery("(max-width:600px)");
  const recaptchaRef = useRef();
  const addCardStripeRef = useRef(null);

  /* ── payment method config from localStorage (set at login) ── */
  const paymentMethodExternal = useMemo(
    () => JSON.parse(localStorage.getItem("paymentMethodCreditCard") || "null"),
    []
  );
  const achPaymentMethodExternal = useMemo(
    () => JSON.parse(localStorage.getItem("paymentMethodAch") || "null"),
    []
  );
  const orgId = localStorage.getItem("orgId");
  const accountId = localStorage.getItem("accountID");

  /* ── build initial charge selections ── */
  const initialSelections = useMemo(() => {
    if (!data) return [];
    if (data?.type === "pay-all") {
      return (data.allData ?? []).map(rowToSelection);
    }
    // pay-now: data is the row spread with type key removed
    const { type, ...row } = data;
    return [rowToSelection(row)];
  }, [data]);

  const initialTotal = useMemo(
    () => initialSelections.reduce((sum, s) => sum + s.amount, 0),
    [initialSelections]
  );

  const [chargeSelections, setChargeSelections] = useState(initialSelections);
  const [TotalForPay, setTotalForPay] = useState(initialTotal);
  const [editedTotalPayment, setEditedTotalPayment] = useState(0);
  const [isLoadingPay, setLoadingPay] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [selectedAchCard, setSelectedAchCard] = useState(null);
  const [convenienceFeeByPayment, setConvenienceFeeByPayment] = useState({
    "credit-card": true,
    ach: true,
  });
  const [error, setError] = useState({ value: true, message: "" });
  const [checkoutDonationLedger, setCheckoutDonationLedger] = useState(null);
  const [donationAmount, setDonationAmount] = useState(0);
  const [donationBoxKey, setDonationBoxKey] = useState(0);
  const [hasDonationError, setHasDonationError] = useState(false);
  const [isLoadingDonationLedger, setIsLoadingDonationLedger] = useState(true);
  const hadFailureRef = useRef(false);

  /* reset when data changes (new charge opened) */
  useEffect(() => {
    setChargeSelections(initialSelections);
    setTotalForPay(initialTotal);
    setEditedTotalPayment(0);
    setError({ value: true, message: "" });
  }, [data]);

  useEffect(() => {
    const fetchCheckoutDonationLedger = async () => {
      try {
        const token = await getAccessTokenSilently();
        const res = await Finance.getCheckoutDonationLedger(token, orgId);
        if (res?.ledger) setCheckoutDonationLedger(res.ledger);
      } catch (_) {} finally {
        setIsLoadingDonationLedger(false);
      }
    };
    fetchCheckoutDonationLedger();
  }, []);

  const enableCreditCardPay = !!paymentMethodExternal?.allow_member;
  const enableAchPay =
    !!achPaymentMethodExternal?.allow_member &&
    checkIsStripe(achPaymentMethodExternal);

  const initialActivePayment = enableCreditCardPay
    ? "credit-card"
    : enableAchPay
    ? "ach"
    : "credit-card";

  const formik = useFormik({
    isInitialValid: false,
    enableReinitialize: true,
    initialValues: {
      accountInfo: {},
      cardsDetails: {},
      resultPage: "",
      totalPayment: 0,
      activePayment: initialActivePayment,
      achDetails: {},
    },
    validationSchema: MemberPersonalDetailsSchema,
    onSubmit: async () => {},
  });

  const activePayment = formik?.values?.activePayment;

  /* pre-fill personal details from portal localStorage; reset donation on open */
  useEffect(() => {
    if (open) {
      try {
        const info = JSON.parse(localStorage.getItem("accountInfo") || "{}");
        formik.setFieldValue("accountInfo", {
          first_name: info?.firstName ?? "",
          last_name: info?.lastName ?? "",
          email: info?.primary_email_address ?? "",
          primary_email_address: info?.primary_email_address ?? "",
        });
      } catch (_) {}
      setDonationAmount(0);
      setHasDonationError(false);
      setDonationBoxKey((k) => k + 1);
    }
  }, [open]);

  const currentPaymentMethod = useMemo(() => {
    if (activePayment === "credit-card") return paymentMethodExternal;
    if (activePayment === "ach") return achPaymentMethodExternal;
    return null;
  }, [activePayment, paymentMethodExternal, achPaymentMethodExternal]);

  const isStripe = useMemo(
    () => checkIsStripe(currentPaymentMethod),
    [currentPaymentMethod]
  );
  const isTranzila = useMemo(
    () => checkIsTranzila(currentPaymentMethod),
    [currentPaymentMethod]
  );

  const calculateConvenienceFeeAmount = useCallback(
    (total) => {
      if (!currentPaymentMethod?.coverConvenienceFee) return 0;
      if (!convenienceFeeByPayment[activePayment]) return 0;
      return calcConvenienceFee(currentPaymentMethod?.convenienceFeeInfo, total);
    },
    [currentPaymentMethod, convenienceFeeByPayment, activePayment]
  );

  const reverseCalculationConvenienceFee = useCallback(
    (value) => {
      if (!currentPaymentMethod?.coverConvenienceFee) return value;
      return currentPaymentMethod?.convenienceFeeInfo?.type === "%"
        ? value / (1 + currentPaymentMethod?.convenienceFeeInfo?.amount / 100)
        : value - (currentPaymentMethod?.convenienceFeeInfo?.amount ?? 0);
    },
    [currentPaymentMethod]
  );

  /* ── mirrors ChargeExternalLink calculateDueAmount ── */
  const calculateDueAmount = useCallback(() => {
    return chargeSelections.reduce((sum, val) => {
      return sum + (val?.installmentStatus && val?.installment?.value
        ? val.amount / val.installment.value
        : val.amount);
    }, 0);
  }, [chargeSelections]);

  const calculationTotalPaymentWithCV = useCallback(() => {
    const base = TotalForPay > 0 ? TotalForPay : 0;
    return base + calculateConvenienceFeeAmount(base) + donationAmount;
  }, [TotalForPay, donationAmount, calculateConvenienceFeeAmount]);

  /* ── mirrors ChargeExternalLink calculateDueAmountWithCF ── */
  const calculateDueAmountWithCF = useCallback(() => {
    const baseAmount = calculateDueAmount();
    const base = editedTotalPayment || baseAmount;
    const cf = calculateConvenienceFeeAmount(base);
    return baseAmount + (currentPaymentMethod?.coverConvenienceFee ? cf : 0) + donationAmount;
  }, [calculateDueAmount, donationAmount, editedTotalPayment, calculateConvenienceFeeAmount, currentPaymentMethod]);

  const calculationPayment = useCallback(() => {
    const total = calculationTotalPaymentWithCV();
    formik.setFieldValue("totalPayment", +total.toFixed(2));
    return total;
  }, [calculationTotalPaymentWithCV, formik]);

  useEffect(() => {
    setEditedTotalPayment(0);
    calculationPayment();
  }, [convenienceFeeByPayment, activePayment, TotalForPay, donationAmount]);

  useEffect(() => {
    if (formik.values?.resultPage === "" && hadFailureRef.current) {
      hadFailureRef.current = false;
      setDonationAmount(0);
      setHasDonationError(false);
      setDonationBoxKey((k) => k + 1);
    }
  }, [formik.values?.resultPage]);

  const totalPaymentForCV = editedTotalPayment || TotalForPay;
  const convenienceFeeAmount = calculateConvenienceFeeAmount(totalPaymentForCV);

  const dueAmountWithCF = useMemo(() => {
    if (editedTotalPayment > 0) return +formik?.values?.totalPayment;
    return calculateDueAmountWithCF();
  }, [editedTotalPayment, formik?.values?.totalPayment, calculateDueAmountWithCF, chargeSelections, donationAmount]);

  const handleGetStripeToken = async () => {
    const tokenId = await addCardStripeRef?.current?.handleGetToken();
    return tokenId ?? "";
  };

  const PayNow = useCallback(async () => {
    if (!formik?.values?.activePayment) return;

    if (hasDonationError) {
      enqueueSnackbar("Please fix the donation amount before proceeding", { variant: "error" });
      return;
    }

    if (!selectedCard && activePayment === "credit-card") {
      enqueueSnackbar("Please select a credit card", { variant: "error" });
      return;
    }
    if (!selectedAchCard && activePayment === "ach") {
      enqueueSnackbar("Please select an ACH card", { variant: "error" });
      return;
    }

    let tokenId = "";
    if (isStripe && activePayment === "credit-card") {
      tokenId = await handleGetStripeToken();
    }

    const payload = {
      token: tokenId || undefined,
      coverFee: convenienceFeeByPayment[activePayment],
      cardId:
        activePayment === "credit-card"
          ? selectedCard
          : activePayment === "ach"
          ? selectedAchCard
          : undefined,
      personalDetails: {
        firstName: formik?.values?.accountInfo?.first_name,
        lastName: formik?.values?.accountInfo?.last_name,
        email: formik?.values?.accountInfo?.email,
      },
      charges: chargeSelections.map((val) => ({
        chargeId: val.chargeId,
        numberOfInstallment: val?.installmentStatus ? val?.installment?.value : undefined,
      })),
      ...(donationAmount > 0 && checkoutDonationLedger && {
        checkoutDonation: {
          ledger_id: checkoutDonationLedger._id,
          ledger_name: checkoutDonationLedger.ledger_name,
          amount: donationAmount,
          account_receivable: checkoutDonationLedger.account_receivable,
        },
      }),
      accountId,
      totalPaid: editedTotalPayment
        ? +formik?.values?.totalPayment
        : chargeSelections.some((v) => v?.installmentStatus)
        ? dueAmountWithCF
        : undefined,
      paymentMethodId:
        activePayment === "credit-card"
          ? paymentMethodExternal?._id
          : activePayment === "ach"
          ? achPaymentMethodExternal?._id
          : undefined,
    };

    if (TotalForPay <= 0) {
      enqueueSnackbar("There is no payment to pay", { variant: "error" });
      return;
    }

    setLoadingPay(true);
    try {
      const token = await getAccessTokenSilently();
      await Finance.payExternalLink(payload, token, orgId);
      formik.setFieldValue("resultPage", "success");
      formik.setFieldValue("email", formik?.values?.accountInfo?.email);
      setTotalForPay(0);
      window.dispatchEvent(new CustomEvent("save-transaction"));
    } catch (_) {
      hadFailureRef.current = true;
      formik.setFieldValue("resultPage", "failed");
    } finally {
      setLoadingPay(false);
    }
  }, [
    activePayment,
    chargeSelections,
    TotalForPay,
    formik,
    selectedCard,
    selectedAchCard,
    convenienceFeeByPayment,
    isStripe,
    editedTotalPayment,
    accountId,
    orgId,
    paymentMethodExternal,
    achPaymentMethodExternal,
    hasDonationError,
    enqueueSnackbar,
  ]);

  const handleClose = () => {
    formik.setFieldValue("resultPage", "");
    onClose();
  };

  /* ── result page ── */
  if (formik.values?.resultPage !== "") {
    return (
      <Dialog open={open} onClose={handleClose} fullWidth fullScreen={isMobile} maxWidth="sm"
        PaperProps={{ style: { height: isMobile ? "100%" : 660 } }}
      >
        <PaymentResultPage
          result={formik.values?.resultPage}
          formik={formik}
          setTotalForPay={setTotalForPay}
          setChecked={() => {}}
          setChargeSelections={(v) => {
            setChargeSelections(v);
            handleClose();
          }}
        />
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      fullScreen={isMobile}
      maxWidth="lg"
      className="portal-pay-now"
      PaperProps={{ style: { borderRadius: isMobile ? 0 : 12 } }}
    >
      <DialogTitle style={{ paddingBottom: 8, paddingRight: 48, paddingLeft: isMobile ? 16 : 24, position: "relative" }}>
        <span style={{ fontFamily: FontFamilies.General, fontWeight: FontWeights.SemiBold, fontSize: isMobile ? Fonts.GeneralSubHeaderFont.fontSize : Fonts.GeneralHeaderFont.fontSize }}>
          {data?.type === "pay-all" ? "Pay Selected Charges" : "Pay Now"}
        </span>
        <IconButton
          onClick={handleClose}
          size="small"
          style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent style={{ padding: isMobile ? "8px 8px 16px" : "16px 24px 24px" }}>
        <Grid container spacing={isMobile ? 1 : 3}>
          {/* Left: summary */}
          <Grid item xs={12} md={7}>
            <Paper elevation={0} style={{ border: "1px solid #E2E8F0", borderRadius: isMobile ? 8 : 10 }}>
              <SummaryCard
                chargeSelections={chargeSelections}
                TotalForPay={TotalForPay}
                currentPaymentMethod={currentPaymentMethod}
                formik={formik}
                setEditedTotalPayment={setEditedTotalPayment}
                editedTotalPayment={editedTotalPayment}
                calculateConvenienceFeeAmount={calculateConvenienceFeeAmount}
                reverseCalculationConvenienceFee={reverseCalculationConvenienceFee}
                calculationTotalPaymentWithCV={calculationTotalPaymentWithCV}
                allowEditTotalPayment={false}
                setChargeSelections={setChargeSelections}
                calculationPayment={calculationPayment}
                calculateDueAmount={calculateDueAmount}
                calculateDueAmountWithCF={calculateDueAmountWithCF}
                dueAmountWithCF={dueAmountWithCF}
                isThereInstallmentPayment={() =>
                  chargeSelections.some((v) => v?.installmentStatus)
                }
                isInstallmentEnabled={
                  !!(paymentMethodExternal?.allowMemberInstallment ||
                    achPaymentMethodExternal?.allowMemberInstallment)
                }
                setActivePaymentAfter={() => {}}
                activePayment={activePayment}
                convenienceFeeByPayment={convenienceFeeByPayment}
                setConvenienceFeeByPayment={setConvenienceFeeByPayment}
              />
            </Paper>

            <Paper elevation={0} style={{ border: "1px solid #E2E8F0", borderRadius: isMobile ? 8 : 10, marginTop: isMobile ? 8 : 16 }}>
              <PersonalDetailsCard
                formik={formik}
                isAuthenticated
                handleLogout={() => {}}
                showZipCode={false}
              />
            </Paper>

          </Grid>

          {/* Right: payment */}
          <Grid item xs={12} md={5}>
            <DonationBox
              ledger={checkoutDonationLedger}
              loading={isLoadingDonationLedger}
              onDonationChange={(amount, ledger) => { setDonationAmount(amount); if (ledger) setCheckoutDonationLedger(ledger); }}
              resetKey={donationBoxKey}
              onErrorChange={setHasDonationError}
            />
            <Paper elevation={0} style={{ border: "1px solid #E2E8F0", borderRadius: isMobile ? 8 : 10, marginTop: (checkoutDonationLedger || isLoadingDonationLedger) ? 16 : 0 }}>
              <PaymentCard
                TotalForPay={TotalForPay + donationAmount}
                formik={formik}
                setError={setError}
                error={error}
                PayNow={PayNow}
                isLoadingPay={isLoadingPay}
                setLoadingPay={setLoadingPay}
                isStripe={isStripe}
                isTranzila={isTranzila}
                currentPaymentMethod={currentPaymentMethod}
                dueAmountWithCF={dueAmountWithCF}
                isAuthenticated
                recaptchaRef={recaptchaRef}
                setSelectedCard={setSelectedCard}
                selectedCard={selectedCard}
                setSelectedAchCard={setSelectedAchCard}
                selectedAchCard={selectedAchCard}
                enableCreditCardPay={enableCreditCardPay}
                enableAchPay={enableAchPay}
                disabledAchPay={!enableAchPay}
                displayAchTooltip=""
                disabledCreditCardPay={!enableCreditCardPay}
                addCardStripeRef={addCardStripeRef}
                publicKey={currentPaymentMethod?.paymentGateway?.keys?.publicKey}
                orgId={orgId}
              />
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default PortalPayNowModal;
